import React, { useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AeroTable3 = ({ apiData }) => {
  const pdfRef = useRef();

  if (apiData.length === 0) {
    return <p>No data available for the employment trend.</p>;
  }

  // Extract years and employment counts for all sexes
  const years = Object.keys(apiData[0]).filter((key) => /^\d{4}$/.test(key));
  const trendData = apiData[0]; // Assume data is structured with a single entry for all sexes

  const chartData = {
    labels: years.filter((year) => Number(year) <= 2024),
    datasets: [
      {
        label: "All Sexes Employment",
        data: years.filter((year) => Number(year) <= 2024)
          .map((year) => trendData[year]),
        backgroundColor: "rgba(1, 76, 178)",
        borderColor: "rgba(1, 76, 178, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Employment Trend (2018-2024)',
        font: {
          size: 14,
          weight: 'bold'
        },
        padding: { top: 10, bottom: 10 }
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 11 },
          boxWidth: 12,
          padding: 10
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Employees',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      }
    }
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    const originalWidth = input.style.width;
    input.style.width = "1200px";

    html2canvas(input, { scale: 3 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add header and footer to the PDF

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });

      pdf.setFontSize(12);
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(0, 0, 0);
      pdf.line(10, 35, 200, 35); // Horizontal line after header

      const headerHeight = 38;
      pdf.addImage(imgData, "PNG", 5, headerHeight, imgWidth, imgHeight); // Add chart image

      pdf.line(10, 260, 200, 260); // Horizontal line before footer

      const footerStartY = 265;
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 122, 255);
      pdf.text("Built by", 55, footerStartY + 10);
      pdf.addImage(bodex, "PNG", 68, footerStartY + 4, 44, 10);
      pdf.text("using", 112, footerStartY + 10);
      pdf.addImage(kpi, "PNG", 125, footerStartY, 20, 20);
      pdf.text(" 20250916.v3.198", 148, footerStartY + 10);

      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString();
      pdf.setFontSize(12);
      pdf.text(`Downloaded on: ${formattedDate}`, 150, 295); // Bottom-right corner date

      const currentURL = window.location.href;
      pdf.text(`Downloaded from: ${currentURL}`, 10, 295);

      pdf.save("employment-trend-dashboard.pdf");
      input.style.width = originalWidth;
    });
  };

  // Calculate summary metrics
  const employmentData = years.filter((year) => Number(year) <= 2024).map(year => trendData[year]);
  const currentEmployment = employmentData[employmentData.length - 1];
  const initialEmployment = employmentData[0];
  const growthPercent = ((currentEmployment - initialEmployment) / initialEmployment * 100).toFixed(1);
  
  // Find year with highest employment
  let maxEmployment = 0;
  let peakYear = "2018";
  years.filter((year) => Number(year) <= 2024).forEach(year => {
    if (trendData[year] > maxEmployment) {
      maxEmployment = trendData[year];
      peakYear = year;
    }
  });

  // Find year with highest growth
  let maxGrowth = 0;
  let bestGrowthYear = "2018";
  for (let i = 1; i < employmentData.length; i++) {
    const currentYear = years.filter((year) => Number(year) <= 2024)[i];
    const prevYear = years.filter((year) => Number(year) <= 2024)[i-1];
    const currentValue = trendData[currentYear];
    const prevValue = trendData[prevYear];
    const growth = ((currentValue - prevValue) / prevValue) * 100;
    if (growth > maxGrowth) {
      maxGrowth = growth;
      bestGrowthYear = currentYear;
    }
  }

  return (
    <div className="max-w-full mx-auto p-4">
      <div ref={pdfRef} className="content-to-export p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg w-full max-w-full overflow-hidden">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center text-gray-800">Employment Trend (Yearly)</h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Total Growth Since 2018
            </p>
            <p className="text-lg font-bold text-blue-800">+{growthPercent}%</p>
          </div>
          <div className="p-3 bg-green-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Peak Employment Year
            </p>
            <p className="text-lg font-bold text-green-800">{peakYear}</p>
          </div>
          <div className="p-3 bg-purple-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">2024 Employment</p>
            <p className="text-lg font-bold text-purple-800">
              {currentEmployment.toLocaleString()} 👥
            </p>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-center">
          <div className="p-3 bg-yellow-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Best Growth Year</p>
            <p className="text-lg font-bold text-yellow-800">{bestGrowthYear} (+{maxGrowth.toFixed(1)}%)</p>
          </div>
          <div className="p-3 bg-indigo-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Peak Employment</p>
            <p className="text-lg font-bold text-indigo-800">{maxEmployment.toLocaleString()}</p>
          </div>
        </div>

        {/* Table + Chart Side-by-Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Table */}
          <div className="w-full">
            <div className="overflow-x-auto">
              <Table striped bordered hover responsive className="w-full border-collapse text-center shadow-sm rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-sm">Year</th>
                    <th className="p-2 text-sm">All Sexes Employment</th>
                  </tr>
                </thead>
                <tbody>
                  {years
                    .filter((year) => year <= 2024)
                    .map((year, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="p-2 text-sm">{year}</td>
                        <td className="p-2 text-sm">{trendData[year].toLocaleString()}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-sm p-3">
              <div className="overflow-x-auto">
                <div className="relative" style={{ height: '350px', minHeight: '280px', minWidth: '400px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg text-sm"
        >
          <span>Download as PDF</span>
          <img className="w-[1.2rem]" src={pdf} alt="PDF Icon" />
        </button>
      </div>
    </div>
  );
};

export default AeroTable3;
